import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageSquareText } from "lucide-react";
import { api, fmtEuro } from "@/lib/api";
import { PublicShell } from "@/components/PublicShell";
import { ScoreBlock, Distribution } from "@/components/RatingSummary";
import { ReviewCard } from "@/components/ReviewCard";
import { GuaranteeSeal } from "@/components/GuaranteeSeal";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const ReviewList = ({ type, rating, sort }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const load = useCallback(async (p) => {
    const params = { type, sort, page: p, limit: 10, ...(rating ? { rating } : {}) };
    const { data } = await api.get("/public/reviews", { params });
    setItems((prev) => (p === 1 ? data.items : [...prev, ...data.items]));
    setTotal(data.total);
    setPage(p);
  }, [type, rating, sort]);

  useEffect(() => { load(1); }, [load]);

  if (!items.length) {
    return (
      <div className="bg-white border border-dashed border-gray-300 rounded-lg p-14 text-center" data-testid="reviews-empty">
        <MessageSquareText className="mx-auto text-gray-400" />
        <p className="mt-3 font-semibold">Nessuna recensione da mostrare</p>
        <p className="text-sm text-gray-600 mt-1">Le recensioni verificate appariranno qui dopo la moderazione.</p>
      </div>
    );
  }
  return (
    <div className="space-y-5" data-testid="reviews-list">
      {items.map((r, i) => <ReviewCard key={r.id} r={r} index={i} />)}
      {items.length < total && (
        <Button variant="outline" className="rounded-full" onClick={() => load(page + 1)} data-testid="reviews-load-more">
          Mostra altre recensioni
        </Button>
      )}
    </div>
  );
};

const GuaranteeCard = ({ s }) => (
  <div className="bg-emerald-900 text-white rounded-lg p-8 relative overflow-hidden" data-testid="guarantee-card">
    <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full border-[18px] border-white/5" aria-hidden="true" />
    <GuaranteeSeal dark size="lg" />
    <p className="mt-6 text-emerald-50 leading-relaxed text-[15px]">
      Ogni ordine è protetto fino a <strong className="text-white">{fmtEuro(s.guarantee_max_amount)}</strong> per{" "}
      <strong className="text-white">{s.guarantee_days} giorni</strong>: se il tuo ordine non arriva, apri una pratica e ce ne occupiamo noi.
    </p>
    <Link to="/garanzia" data-testid="guarantee-card-link" className="mt-6 inline-flex items-center gap-2 bg-white text-emerald-900 font-semibold rounded-full px-5 py-2.5 hover:bg-emerald-50 transition-colors">
      Scopri la garanzia <ArrowRight size={16} />
    </Link>
  </div>
);

export default function PublicReviews() {
  const [s, setS] = useState(null);
  const [type, setType] = useState("shop");
  const [rating, setRating] = useState(null);
  const [sort, setSort] = useState("recent");

  useEffect(() => { api.get("/public/summary").then((r) => setS(r.data)); }, []);
  if (!s) return <PublicShell><div className="h-[60vh]" /></PublicShell>;

  return (
    <PublicShell>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-3xl fade-up">
          Cosa dicono i clienti di <span className="text-emerald-900">{s.shop_name}</span>
        </h1>
        <p className="mt-4 text-base md:text-lg text-gray-600 max-w-2xl fade-up" style={{ animationDelay: "80ms" }}>
          Solo opinioni reali, raccolte dopo un acquisto confermato.
        </p>
        <div className="mt-12 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 bg-white border border-gray-200 rounded-lg p-8 fade-up" style={{ animationDelay: "120ms" }}>
            <ScoreBlock summary={s} />
          </div>
          <div className="lg:col-span-4 bg-white border border-gray-200 rounded-lg p-6 fade-up" style={{ animationDelay: "180ms" }}>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 font-semibold mb-4 px-2">Distribuzione voti</p>
            <Distribution summary={s} active={rating} onPick={setRating} />
          </div>
          <div className="lg:col-span-3 fade-up" style={{ animationDelay: "240ms" }}><GuaranteeCard s={s} /></div>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 mt-16">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <Tabs value={type} onValueChange={(v) => { setType(v); setRating(null); }}>
            <TabsList>
              <TabsTrigger value="shop" data-testid="tab-shop-reviews">Negozio</TabsTrigger>
              <TabsTrigger value="product" data-testid="tab-product-reviews">Prodotti</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-3">
            {rating && (
              <button className="text-sm text-emerald-900 font-semibold hover:underline" onClick={() => setRating(null)} data-testid="clear-rating-filter">
                {rating} stelle ✕
              </button>
            )}
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-44 bg-white" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Più recenti</SelectItem>
                <SelectItem value="highest">Voto più alto</SelectItem>
                <SelectItem value="lowest">Voto più basso</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="max-w-3xl"><ReviewList type={type} rating={rating} sort={sort} /></div>
      </section>
    </PublicShell>
  );
}
